/**
 * BibleChallengesManagement - Admin UI for creating and managing Bible challenges
 * Supports reading challenges, quizzes, memorization, and trivia competitions
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

// Icons
import {
  BookOpen, Trophy, Users, Plus, Pencil, Trash2, Calendar, Target,
  Award, Loader2, CheckCircle2, XCircle, HelpCircle, BookMarked,
  Brain, Clock, Star, TrendingUp, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helpers
const challengesAPI = {
  list: async (token, eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/bible-challenges?active_only=false`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch challenges');
    return res.json();
  },
  create: async (token, eventId, data) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/bible-challenges`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...data, event_id: eventId })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to create challenge');
    }
    return res.json();
  },
  update: async (token, challengeId, data) => {
    const res = await fetch(`${API_URL}/api/bible-challenges/${challengeId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update challenge');
    return res.json();
  },
  delete: async (token, challengeId) => {
    const res = await fetch(`${API_URL}/api/bible-challenges/${challengeId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to delete challenge');
    return res.json();
  },
  getDetails: async (challengeId) => {
    const res = await fetch(`${API_URL}/api/bible-challenges/${challengeId}`);
    if (!res.ok) throw new Error('Failed to fetch challenge details');
    return res.json();
  },
  getLeaderboard: async (eventId) => {
    const res = await fetch(`${API_URL}/api/events/${eventId}/bible-leaderboard`);
    if (!res.ok) throw new Error('Failed to fetch leaderboard');
    return res.json();
  },
  getTrivia: async (count = 10, difficulty = 'medium') => {
    const res = await fetch(`${API_URL}/api/trivia/bible?count=${count}&difficulty=${difficulty}`);
    if (!res.ok) throw new Error('Failed to fetch trivia');
    return res.json();
  }
};

// Challenge type icons
const CHALLENGE_TYPE_ICONS = {
  reading: BookOpen,
  quiz: HelpCircle,
  memorization: Brain,
  trivia: Trophy
};

const CHALLENGE_TYPES = [
  { value: 'reading', label: 'Reading Challenge', description: 'Complete chapters or books' },
  { value: 'quiz', label: 'Bible Quiz', description: 'Answer questions about scripture' },
  { value: 'memorization', label: 'Memorization', description: 'Memorize verses' },
  { value: 'trivia', label: 'Bible Trivia', description: 'Fun trivia competition' }
];

// Books of the Bible for selection
const BIBLE_BOOKS = [
  // Old Testament
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges',
  'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
  'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs', 'Ecclesiastes',
  'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel',
  'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
  'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  // New Testament
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', '2 Corinthians',
  'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James', '1 Peter', '2 Peter',
  '1 John', '2 John', '3 John', 'Jude', 'Revelation'
];

// ============== Challenge Form Dialog ==============
const ChallengeFormDialog = ({ eventId, challenge, open, onOpenChange, onSuccess }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const isEdit = !!challenge;

  const [form, setForm] = useState({
    title: challenge?.title || '',
    description: challenge?.description || '',
    challenge_type: challenge?.challenge_type || 'reading',
    start_date: challenge?.start_date || '',
    end_date: challenge?.end_date || '',
    points_per_completion: challenge?.points_per_completion || 10,
    is_team_based: challenge?.is_team_based || false,
    max_participants: challenge?.max_participants || null,
    target_chapters: challenge?.target_chapters || [],
    quiz_questions: challenge?.quiz_questions || []
  });

  const [selectedBook, setSelectedBook] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_answer: '',
    scripture_ref: '',
    difficulty: 'medium',
    explanation: ''
  });

  const createMutation = useMutation({
    mutationFn: (data) => challengesAPI.create(token, eventId, data),
    onSuccess: () => {
      toast.success('Challenge created successfully!');
      queryClient.invalidateQueries(['bible-challenges', eventId]);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err) => toast.error(err.message)
  });

  const updateMutation = useMutation({
    mutationFn: (data) => challengesAPI.update(token, challenge.id, data),
    onSuccess: () => {
      toast.success('Challenge updated successfully!');
      queryClient.invalidateQueries(['bible-challenges', eventId]);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err) => toast.error(err.message)
  });

  const handleSubmit = () => {
    if (!form.title) {
      toast.error('Title is required');
      return;
    }

    if (isEdit) {
      updateMutation.mutate(form);
    } else {
      createMutation.mutate(form);
    }
  };

  const addChapter = () => {
    if (selectedBook && selectedChapter) {
      const chapter = { book: selectedBook, chapter: parseInt(selectedChapter) };
      if (!form.target_chapters.some(tc => tc.book === chapter.book && tc.chapter === chapter.chapter)) {
        setForm({
          ...form,
          target_chapters: [...form.target_chapters, chapter]
        });
      }
      setSelectedChapter('');
    }
  };

  const removeChapter = (index) => {
    setForm({
      ...form,
      target_chapters: form.target_chapters.filter((_, i) => i !== index)
    });
  };

  const addQuestion = () => {
    if (!newQuestion.question || !newQuestion.correct_answer) {
      toast.error('Question and correct answer are required');
      return;
    }

    setForm({
      ...form,
      quiz_questions: [...form.quiz_questions, { ...newQuestion }]
    });

    setNewQuestion({
      question: '',
      options: ['', '', '', ''],
      correct_answer: '',
      scripture_ref: '',
      difficulty: 'medium',
      explanation: ''
    });
  };

  const removeQuestion = (index) => {
    setForm({
      ...form,
      quiz_questions: form.quiz_questions.filter((_, i) => i !== index)
    });
  };

  const loadTrivia = async () => {
    try {
      const trivia = await challengesAPI.getTrivia(5, 'medium');
      setForm({
        ...form,
        quiz_questions: [
          ...form.quiz_questions,
          ...trivia.map(q => ({
            question: q.question,
            options: q.options,
            correct_answer: q.correct_answer,
            scripture_ref: q.scripture_ref,
            difficulty: q.difficulty,
            explanation: ''
          }))
        ]
      });
      toast.success('Added 5 trivia questions!');
    } catch (err) {
      toast.error('Failed to load trivia questions');
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {isEdit ? 'Edit Challenge' : 'Create New Challenge'}
          </DialogTitle>
          <DialogDescription>
            Set up a Bible challenge for your event attendees
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Challenge Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., 30-Day Gospel Challenge"
                  data-testid="challenge-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Challenge Type</Label>
                <Select
                  value={form.challenge_type}
                  onValueChange={(v) => setForm({ ...form, challenge_type: v })}
                >
                  <SelectTrigger data-testid="challenge-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHALLENGE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          {React.createElement(CHALLENGE_TYPE_ICONS[type.value], { className: "h-4 w-4" })}
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what participants need to do..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="points">Points per Completion</Label>
                <Input
                  id="points"
                  type="number"
                  min="1"
                  value={form.points_per_completion}
                  onChange={(e) => setForm({ ...form, points_per_completion: parseInt(e.target.value) || 10 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_participants">Max Participants</Label>
                <Input
                  id="max_participants"
                  type="number"
                  min="0"
                  value={form.max_participants || ''}
                  onChange={(e) => setForm({ ...form, max_participants: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Unlimited"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="team_based"
                  checked={form.is_team_based}
                  onCheckedChange={(checked) => setForm({ ...form, is_team_based: checked })}
                />
                <Label htmlFor="team_based">Team-based</Label>
              </div>
            </div>
          </div>

          {/* Reading Challenge - Chapter Selection */}
          {form.challenge_type === 'reading' && (
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <BookMarked className="h-4 w-4" />
                Target Chapters
              </h4>
              <p className="text-sm text-muted-foreground">
                Select which chapters participants need to read to complete the challenge.
              </p>

              <div className="flex gap-2">
                <Select value={selectedBook} onValueChange={setSelectedBook}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select book" />
                  </SelectTrigger>
                  <SelectContent>
                    {BIBLE_BOOKS.map(book => (
                      <SelectItem key={book} value={book}>{book}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="1"
                  className="w-24"
                  placeholder="Chapter"
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                />
                <Button variant="outline" onClick={addChapter} disabled={!selectedBook || !selectedChapter}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {form.target_chapters.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.target_chapters.map((tc, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {tc.book} {tc.chapter}
                      <XCircle
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={() => removeChapter(i)}
                      />
                    </Badge>
                  ))}
                </div>
              )}

              {form.target_chapters.length === 0 && (
                <p className="text-sm text-amber-600">
                  No chapters selected. Any chapter read will count towards progress.
                </p>
              )}
            </div>
          )}

          {/* Quiz Challenge - Questions */}
          {(form.challenge_type === 'quiz' || form.challenge_type === 'trivia') && (
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  Quiz Questions ({form.quiz_questions.length})
                </h4>
                <Button variant="outline" size="sm" onClick={loadTrivia}>
                  <Loader2 className="h-4 w-4 mr-2" />
                  Load Trivia
                </Button>
              </div>

              {/* Existing Questions */}
              {form.quiz_questions.length > 0 && (
                <ScrollArea className="h-48 border rounded-lg p-2">
                  {form.quiz_questions.map((q, i) => (
                    <div key={i} className="flex items-start justify-between p-2 hover:bg-muted rounded">
                      <div>
                        <p className="text-sm font-medium">{i + 1}. {q.question}</p>
                        <p className="text-xs text-muted-foreground">Answer: {q.correct_answer}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeQuestion(i)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </ScrollArea>
              )}

              {/* Add New Question */}
              <div className="space-y-3 border-t pt-4">
                <h5 className="text-sm font-medium">Add New Question</h5>
                <Input
                  placeholder="Enter your question..."
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-2">
                  {newQuestion.options.map((opt, i) => (
                    <Input
                      key={i}
                      placeholder={`Option ${i + 1}`}
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...newQuestion.options];
                        newOptions[i] = e.target.value;
                        setNewQuestion({ ...newQuestion, options: newOptions });
                      }}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Input
                    placeholder="Correct answer"
                    value={newQuestion.correct_answer}
                    onChange={(e) => setNewQuestion({ ...newQuestion, correct_answer: e.target.value })}
                  />
                  <Input
                    placeholder="Scripture ref (e.g., John 3:16)"
                    value={newQuestion.scripture_ref}
                    onChange={(e) => setNewQuestion({ ...newQuestion, scripture_ref: e.target.value })}
                  />
                  <Select
                    value={newQuestion.difficulty}
                    onValueChange={(v) => setNewQuestion({ ...newQuestion, difficulty: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy (5 pts)</SelectItem>
                      <SelectItem value="medium">Medium (10 pts)</SelectItem>
                      <SelectItem value="hard">Hard (20 pts)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="secondary" size="sm" onClick={addQuestion}>
                  <Plus className="h-4 w-4 mr-2" /> Add Question
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending} data-testid="save-challenge-btn">
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {isEdit ? 'Update Challenge' : 'Create Challenge'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Leaderboard Card ==============
const LeaderboardCard = ({ eventId }) => {
  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['bible-leaderboard', eventId],
    queryFn: () => challengesAPI.getLeaderboard(eventId),
    staleTime: 30000
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const entries = leaderboard?.leaderboard || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          Event Leaderboard
        </CardTitle>
        <CardDescription>
          {leaderboard?.total_participants || 0} participants across {leaderboard?.total_challenges || 0} challenges
        </CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No participants yet</p>
          </div>
        ) : (
          <ScrollArea className="h-64">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Rank</TableHead>
                  <TableHead>Participant</TableHead>
                  <TableHead className="text-right">Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, i) => (
                  <TableRow key={entry._id}>
                    <TableCell>
                      {i < 3 ? (
                        <Crown className={`h-5 w-5 ${
                          i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : 'text-amber-700'
                        }`} />
                      ) : (
                        <span className="text-muted-foreground">{i + 1}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {entry.user_name?.[0] || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{entry.user_name || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">
                            {entry.chapters_read || 0} chapters • {entry.quizzes_answered || 0} quizzes
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {entry.total_points || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Main Component ==============
const BibleChallengesManagement = ({ eventId }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ['bible-challenges', eventId],
    queryFn: () => challengesAPI.list(token, eventId),
    enabled: !!token && !!eventId
  });

  const deleteMutation = useMutation({
    mutationFn: (challengeId) => challengesAPI.delete(token, challengeId),
    onSuccess: () => {
      toast.success('Challenge deleted');
      queryClient.invalidateQueries(['bible-challenges', eventId]);
    },
    onError: (err) => toast.error(err.message)
  });

  const handleDelete = (challenge) => {
    if (window.confirm(`Delete "${challenge.title}"? This cannot be undone.`)) {
      deleteMutation.mutate(challenge.id);
    }
  };

  const TypeIcon = (type) => CHALLENGE_TYPE_ICONS[type] || BookOpen;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" />
            Bible Challenges
          </h2>
          <p className="text-muted-foreground">
            Create reading plans, quizzes, and competitions for your event
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-challenge-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Challenge
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Challenges</p>
                <p className="text-2xl font-bold">
                  {challenges.filter(c => c.is_active).length}
                </p>
              </div>
              <Trophy className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Participants</p>
                <p className="text-2xl font-bold">
                  {challenges.reduce((acc, c) => acc + (c.participants_count || 0), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completions</p>
                <p className="text-2xl font-bold">
                  {challenges.reduce((acc, c) => acc + (c.completions_count || 0), 0)}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Quiz Questions</p>
                <p className="text-2xl font-bold">
                  {challenges.reduce((acc, c) => acc + (c.quiz_questions?.length || 0), 0)}
                </p>
              </div>
              <HelpCircle className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Challenges List */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs defaultValue="active" className="w-full">
            <TabsList>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="all">All Challenges</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4">
              {challenges.filter(c => c.is_active).length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                    <h3 className="font-medium mb-2">No Active Challenges</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create your first Bible challenge to engage attendees
                    </p>
                    <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Create Challenge
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                challenges.filter(c => c.is_active).map(challenge => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    onEdit={() => setEditingChallenge(challenge)}
                    onDelete={() => handleDelete(challenge)}
                    onViewDetails={() => setSelectedChallenge(challenge)}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="all" className="space-y-4">
              {challenges.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No challenges created yet</p>
                  </CardContent>
                </Card>
              ) : (
                challenges.map(challenge => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    onEdit={() => setEditingChallenge(challenge)}
                    onDelete={() => handleDelete(challenge)}
                    onViewDetails={() => setSelectedChallenge(challenge)}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Leaderboard */}
        <div>
          <LeaderboardCard eventId={eventId} />
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <ChallengeFormDialog
        eventId={eventId}
        challenge={editingChallenge}
        open={showCreateDialog || !!editingChallenge}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingChallenge(null);
          }
        }}
        onSuccess={() => {
          setShowCreateDialog(false);
          setEditingChallenge(null);
        }}
      />

      {/* Challenge Details Dialog */}
      {selectedChallenge && (
        <ChallengeDetailsDialog
          challenge={selectedChallenge}
          open={!!selectedChallenge}
          onOpenChange={(open) => !open && setSelectedChallenge(null)}
        />
      )}
    </div>
  );
};

// ============== Challenge Card ==============
const ChallengeCard = ({ challenge, onEdit, onDelete, onViewDetails }) => {
  const TypeIcon = CHALLENGE_TYPE_ICONS[challenge.challenge_type] || BookOpen;
  const completionRate = challenge.participants_count > 0
    ? Math.round((challenge.completions_count / challenge.participants_count) * 100)
    : 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${
              challenge.challenge_type === 'reading' ? 'bg-blue-100 text-blue-600' :
              challenge.challenge_type === 'quiz' ? 'bg-purple-100 text-purple-600' :
              challenge.challenge_type === 'memorization' ? 'bg-amber-100 text-amber-600' :
              'bg-green-100 text-green-600'
            }`}>
              <TypeIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">{challenge.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {challenge.description || 'No description'}
              </p>
              <div className="flex items-center gap-3 mt-2 text-sm">
                <Badge variant={challenge.is_active ? "default" : "secondary"}>
                  {challenge.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="h-3 w-3" /> {challenge.participants_count || 0}
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Award className="h-3 w-3" /> {challenge.points_per_completion} pts
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onViewDetails}>
              <TrendingUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Completion Rate</span>
            <span className="font-medium">{completionRate}%</span>
          </div>
          <Progress value={completionRate} className="h-1.5" />
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Challenge Details Dialog ==============
const ChallengeDetailsDialog = ({ challenge, open, onOpenChange }) => {
  const { data: details, isLoading } = useQuery({
    queryKey: ['challenge-details', challenge.id],
    queryFn: () => challengesAPI.getDetails(challenge.id),
    enabled: open
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{challenge.title}</DialogTitle>
          <DialogDescription>{challenge.description}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold">{details?.participants_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Participants</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold">{details?.completions_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Completions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold">{details?.points_per_completion || 0}</p>
                  <p className="text-xs text-muted-foreground">Points/Completion</p>
                </CardContent>
              </Card>
            </div>

            {/* Leaderboard */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top Participants</CardTitle>
              </CardHeader>
              <CardContent>
                {details?.leaderboard?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No participants yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {details?.leaderboard?.slice(0, 5).map((entry, i) => (
                      <div key={entry._id || i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 text-center font-medium">{i + 1}</span>
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {entry.user_name?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{entry.user_name || 'Anonymous'}</span>
                        </div>
                        <Badge variant="secondary">{entry.points || 0} pts</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BibleChallengesManagement;
